// import { useMutation, useQueryClient } from '@tanstack/react-query';
// import { managerApi } from '../api/houseApi';
// import type { HouseManager, CreateManagerAssignmentData, UpdateManagerAssignmentData } from '../api/houseApi';
// import toast from 'react-hot-toast';

// export const useManagerMutations = () => {
//   const queryClient = useQueryClient();

//   // Assign manager mutation
//   const assignManager = useMutation({
//     mutationFn: (data: CreateManagerAssignmentData) => managerApi.assignManager(data),
//     onSuccess: (newManager: HouseManager) => {
//       // Invalidate and refetch managers list
//       queryClient.invalidateQueries({ queryKey: ['managers'] });
//       queryClient.invalidateQueries({ queryKey: ['houses'] });
//       toast.success(`Manager "${newManager.user.firstName} ${newManager.user.lastName}" assigned successfully!`);
//     },
//     onError: (error: Error) => {
//       toast.error(error.message || 'Failed to assign manager');
//     }
//   });

//   // Update manager assignments mutation
//   const updateManagerAssignments = useMutation({
//     mutationFn: ({ managerId, data }: { managerId: string; data: UpdateManagerAssignmentData }) =>
//       managerApi.updateManagerAssignments(managerId, data),
//     onSuccess: (updatedManager: HouseManager) => {
//       // Update the specific manager in cache
//       queryClient.setQueryData(['managers', updatedManager.id], updatedManager);
//       // Invalidate managers list and houses to ensure consistency
//       queryClient.invalidateQueries({ queryKey: ['managers'] });
//       queryClient.invalidateQueries({ queryKey: ['houses'] });
//       toast.success(`Manager assignments updated successfully!`);
//     },
//     onError: (error: Error) => {
//       toast.error(error.message || 'Failed to update manager assignments');
//     }
//   });

//   // Remove manager from house mutation
//   const removeManagerFromHouse = useMutation({
//     mutationFn: ({ managerId, houseId }: { managerId: string; houseId: string }) =>
//       managerApi.removeManagerFromHouse(managerId, houseId),
//     onSuccess: (_, { managerId, houseId }) => {
//       // Invalidate relevant queries
//       queryClient.invalidateQueries({ queryKey: ['managers'] });
//       queryClient.invalidateQueries({ queryKey: ['managers', managerId] });
//       queryClient.invalidateQueries({ queryKey: ['houses'] });
//       queryClient.invalidateQueries({ queryKey: ['houses', houseId] });
//       toast.success('Manager removed from house successfully!');
//     },
//     onError: (error: Error) => {
//       toast.error(error.message || 'Failed to remove manager from house');
//     }
//   });

//   // Remove manager completely mutation
//   const removeManager = useMutation({
//     mutationFn: (managerId: string) => managerApi.removeManager(managerId),
//     onSuccess: (_, managerId) => {
//       // Remove from cache
//       queryClient.removeQueries({ queryKey: ['managers', managerId] });
//       // Invalidate managers list and houses
//       queryClient.invalidateQueries({ queryKey: ['managers'] });
//       queryClient.invalidateQueries({ queryKey: ['houses'] });
//       toast.success('Manager removed successfully!');
//     },
//     onError: (error: Error) => {
//       toast.error(error.message || 'Failed to remove manager');
//     }
//   });

//   // Helper function to optimistically update manager in cache
//   const optimisticUpdateManager = (id: string, updates: Partial<HouseManager>) => {
//     queryClient.setQueryData(['managers', id], (oldData: HouseManager | undefined) => {
//       if (!oldData) return oldData;
//       return { ...oldData, ...updates };
//     });
//   };

//   // Helper function to invalidate all manager-related queries
//   const invalidateManagerQueries = () => {
//     queryClient.invalidateQueries({ queryKey: ['managers'] });
//     queryClient.invalidateQueries({ queryKey: ['houses'] });
//   };

//   return {
//     // Mutations
//     assignManager,
//     updateManagerAssignments,
//     removeManagerFromHouse,
//     removeManager,

//     // Loading states
//     isAssigning: assignManager.isPending,
//     isUpdatingAssignments: updateManagerAssignments.isPending,
//     isRemovingFromHouse: removeManagerFromHouse.isPending,
//     isRemoving: removeManager.isPending,

//     // Helper functions
//     optimisticUpdateManager,
//     invalidateManagerQueries,

//     // Combined loading state for any manager operation
//     isLoading: assignManager.isPending ||
//                updateManagerAssignments.isPending ||
//                removeManagerFromHouse.isPending ||
//                removeManager.isPending
//   };
// };

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { managerApi } from '../api';
import type { ManagerFiltersType } from '../components/manager/ManagerFilters';

export const useAssignHouseMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      managerId,
      houseIds,
    }: {
      managerId: string;
      houseIds: string[];
    }) => managerApi.assignHouseToManager(managerId, houseIds),

    onSuccess: (_, variables) => {
      // Optionally refetch manager details to update assigned houses list
      queryClient.invalidateQueries({
        queryKey: ['manager', variables.managerId],
      });
    },

    onError: (error: unknown) => {
      const err = error as { message?: string };
      console.error('❌ Failed to assign house:', err.message);
    },
  });
};

export const useDeleteManager = () => {
  const queryClient = useQueryClient();

  return useMutation({
    // ✅ Accept an object with both managerId and filters
    mutationFn: async ({
      managerId,
    }: {
      managerId: string;
      filters?: ManagerFiltersType;
    }) => {
      return managerApi.deleteManager(managerId);
    },

    onSuccess: () => {
      // ✅ Invalidate all manager lists to ensure UI is updated
      queryClient.invalidateQueries({ queryKey: ['managers'] });
    },

    onError: (error: unknown) => {
      const err = error as { message?: string };
      console.error('❌ Error deleting manager:', err.message);
    },
  });
};

export const useInviteManager = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, houseId }: { email: string; houseId?: string }) =>
      managerApi.inviteManager(email, houseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['managers'] });
    },
  });
};
